import { Component, Input, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, NgZone } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { MapService } from '../../map/services/map.service';
import { ImportLocationsComponent } from '../import-locations/import-locations.component';
import { formatString } from '../../utils/string-utils';

import { BaseMap, MapControl, LengthUnit } from '../../models/map.interface';

import { loadModules } from 'esri-loader';

import { CropperComponent } from 'angular-cropperjs';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser

export interface Georef {
	x: number;
	y: number;
	lon: number;
	lat: number;
}

/** 
 * Component allowing alignment of a product against a map projection / coordinate system.
 */
@Component({
	selector: 'geo-reference',
	templateUrl: './geo-reference.component.html',
	styleUrl: './geo-reference.component.css'
})
export class GeoReferenceComponent implements OnInit, OnDestroy, AfterViewInit {

	@Input() product: any;

	@ViewChild('mapContainer') mapContainer!: ElementRef;

	// Access the component class instance
	@ViewChild('angularCropper') angularCropper!: CropperComponent;
	// Access the DOM element (host element of the component)
	@ViewChild('angularCropper', { read: ElementRef }) cropperElementRef!: ElementRef;

	LOCALIZATION: any;

	imageURL: SafeResourceUrl | undefined = undefined;
	imageHeight: number = 0;
	imageWidth: number = 0;
	cropperConfig: any;

	mapId: string = 'georeference-map';
	mapView: any;
	mapLayer: any;
	basemapConfigs: BaseMap[] = [];
	searchWidget: any;

	mapControl!: MapControl;

	search = {
		x: "",
		y: ""
	};

	georef: Georef = {
		x: 0,
		y: 0,
		lon: 0,
		lat: 0
	};
	points: Georef[] = [];
	showPoints: boolean = false;
	readyToAdd: boolean = false;
	inprogress: boolean = false;

	constructor(
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private mapService: MapService,
		private ngbmodalService: NgbActiveModal,
		private ngbModal: NgbModal,
		private ngZone: NgZone
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		this.basemapConfigs = this.configService.getConfig()?.esri?.basemaps || [];
		this.mapControl = this.mapService.getMapControl();
		this.imageURL = this.product.viewerOverviewURL || this.product.viewerURL;
		this.initializeCropper();
	}

	ngAfterViewInit(): void {
		this.initializeMap();
	}

	ngOnDestroy() {
		if (this.mapView) {
			this.mapView.destroy();
		}
	}

	private async initializeMap(): Promise<any> {
		this.mapView = await this.loadMap();
		this.mapService.loadBaseMap(this.mapView);

		this.mapView.when(() => {
			console.log("%s view is ready. All layers loaded.", this.mapId);

			let coords = this.configService.getConfig().esri.center ? this.configService.getConfig().esri.center.split(",") : ["0", "0"];
			this.mapService.centerAndZoomMap(this.mapView, coords, this.configService.getConfig().esri.zoom);

			this.onMapLoaded();
		});
	}

	private async loadMap(): Promise<any> {
		try {
			const [Map, MapView] = await loadModules(['esri/Map', 'esri/views/MapView']);
			const map = new Map({
				basemap: 'topo-vector'
			});

			const view = new MapView({
				container: this.mapContainer.nativeElement,
				map: map,
				center: [-118, 34],
				zoom: 10
			});

			return view;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	async getMapLayer(id: string): Promise<any> {
		const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);

		let layer = this.mapView.map.findLayerById(id);
		if (layer) return layer;

		switch (id) {
			case "georeference":
				layer = new GraphicsLayer({
					id: "georeference"
				});
				break;
		}

		if (layer) {
			this.mapView.map.add(layer);
			console.log("Created new layer with id '%s'", layer.id);
		}
		return layer;
	}

	private async onMapLoaded(): Promise<any> {
		const [Search, Point, Graphic] =
			await loadModules([
				'esri/widgets/Search',
				'esri/geometry/Point',
				'esri/Graphic'
			]);

		this.searchWidget = new Search({
			view: this.mapView,
			container: "locationBar"
		});
		this.searchWidget.on("search-complete", (event: any) => {
			// this.searchWidgetResults = event.results[0].results;
			console.log(event.results[0].results);
		});
		this.searchWidget.on("select-result", (event: any) => {
			// Build a custom feature display. Can probably replace this with a template.
			let content =
				`<div class="map-searchwidget-content">` +
				`<div class="searchwidget-address">${event.result.feature.attributes.Match_addr}</div>` +
				`<div class="searchwidget-address-type">${event.result.feature.attributes.Addr_type} in ${event.result.feature.attributes.City}</div>` +
				`</div>`;

			this.mapView.goTo({
				target: event.result.feature.geometry,
				zoom: 15
			}).then(() => {
				// The popup displays docked by default in narrow viewports
				this.mapView.popup.open({
					title: event.result.name,
					content: content,
					location: event.result.feature.geometry,
					shouldFocus: true
				});
			});
		});


		this.mapLayer = await this.getMapLayer("georeference");

		this.mapView.on("click", (event: any) => {
			this.updateLonLat(event);

			let mapPoint = new Point({
				longitude: event.mapPoint.longitude,
				latitude: event.mapPoint.latitude,
				spatialReference: this.mapView.map.spatialReference
			});
			let markerSymbol = this.mapService.getSymbol('single');
			let pointGraphic = new Graphic({
				geometry: mapPoint,
				symbol: markerSymbol,
				attributes: {
					id: `${this.georef.lon},${this.georef.lat}`
				}
			});
			this.mapLayer.add(pointGraphic);
			// Refresh the layer to update the map
			this.mapLayer.emit("graphics-update");
			this.mapView.goTo(this.mapView.center, { animate: false }); // Jiggle the map to initiate a rerender
			this.readyToAdd = true;
		});
	}

	changeBasemap(basemapName: string): void {
		this.mapService.loadBaseMap(this.mapView, basemapName);
	}

	zoomTo(): void {
		this.mapService.centerAndZoomMap(this.mapView, [this.search.x, this.search.y], 13);
	}

	addPoint(): void {
		this.readyToAdd = false;
		if (this.georef.lon > 180 || this.georef.lon < -180) {
			this.alertsService.error({
				message: "Longitude must be between -180 and 180",
				delay: 5000,
				title: "Longitude out of range"
			});
			return;
		}

		if (this.georef.lat > 90 || this.georef.lat < -90) {
			this.alertsService.error({
				message: "Latitiude must be between -90 and 90",
				delay: 5000,
				title: "Latitude out of range"
			});
			return;
		}

		if (this.georef.x < 0 || this.georef.x > this.imageWidth || this.georef.y < 0 || this.georef.y > this.imageHeight) {
			this.alertsService.error({
				message: "The coordinate point for this image is not in scope.",
				delay: 5000,
				title: "Image coordinate out of range"
			});
			return;
		}

		let point = {
			x: this.georef.x,
			y: this.georef.y,
			lon: this.georef.lon,
			lat: this.georef.lat
		}
		this.points.push(point);
		this.georef = {
			x: 0,
			y: 0,
			lon: 0,
			lat: 0
		};
	}

	removePoint(index: number): void {
		let point = this.points[index];
		this.removeGraphicByCoordinates(point.lat, point.lon);
		this.points.splice(index, 1);
	}

	removeGraphicByCoordinates(latitude: number, longitude: number): void {
		let graphics = this.mapLayer.graphics.items;
		let graphicToRemove = this.mapLayer.graphics.find((graphic: any) =>
			graphic.attributes.id === `${longitude},${latitude}`
		);
		if (graphicToRemove) {
			this.mapLayer.remove(graphicToRemove);
			// Refresh the layer to update the map
			this.mapLayer.emit("graphics-update");
		}
	}

	updateLonLat(event: any): void {
		// The point in webMercator coordinates
		let mapPoint = event.mapPoint;

		// Convert to geographic coordinates (longitude and latitude)
		let normalizedVal = [mapPoint.longitude, mapPoint.latitude];

		this.ngZone.run(() => {
			this.georef.lon = normalizedVal[0];
			this.georef.lat = normalizedVal[1];
		});
	}

	georeference(): void {
		this.inprogress = true;

		let points = [];
		for (let i = 0; i < this.points.length; i++) {
			let point = formatString("{0} {1} {2} {3}", this.points[i].x, this.points[i].y, this.points[i].lon, this.points[i].lat);
			points.push(point);
		}
		
		this.mapService.georeferenceImage(this.product.id, this.product.page, points).then(
			(response: any) => {
				if (!response) {
					this.alertsService.error({
						message: this.LOCALIZATION.georeferenceMessageFailed,
						delay: 5000,
						title: this.LOCALIZATION.georeferenceTitleFailed
					});
				} else {
					this.alertsService.success({
						message: this.LOCALIZATION.georeferenceMessageSuccess,
						delay: 5000,
						title: this.LOCALIZATION.georeferenceTitleSuccess
					});
				}
				this.inprogress = false;
			},
			(err: any) => {
				console.error("Failed to georeference product: ", err);
				this.alertsService.error({
					message: this.LOCALIZATION.georeferenceMessageFailed2,
					delay: 5000,
					title: this.LOCALIZATION.georeferenceTitleFailed
				});
				this.inprogress = false;
			}
		);
	}

	// --------------------------------
	// Cropper image viewer functions
	// --------------------------------

	private initializeCropper() {
		// For options, see: https://github.com/fengyuanchen/cropperjs/blob/v1/README.md#options
		this.cropperConfig = {
			dragMode: 'none',
			toggleDragModeOnDblclick: false,
			aspectRatio: 1,
			viewMode: 1,
			movable: true,
			zoomable: true,
			scalable: true,
			rotatable: true,
			autoCrop: false,
			autoCropArea: 0.8,
			ready: this.getImageDimensions.bind(this),
			cropend: this.onImageClick.bind(this)
		};

		setTimeout(() => {
			// Access image inside the cropper
			const imageEl = this.cropperElementRef.nativeElement.querySelector('img') as HTMLImageElement;
			if (imageEl) {
				imageEl.addEventListener('click', this.onImageClick.bind(this));
			}
		});
	}

	private getImageDimensions(event: any): void {
		const cropper = this.angularCropper?.cropper;
		if (cropper) {
			let imageData = cropper.getImageData();
			this.imageWidth = Math.round(imageData.naturalWidth);
			this.imageHeight = Math.round(imageData.naturalHeight);
			console.log(`Original image size: ${this.imageWidth}x${this.imageHeight}`);
		}
	}

	onImageClick(event: any): void {
		const image = event.detail.originalEvent.target as HTMLImageElement;
		const rect = image.getBoundingClientRect();
		const x = event.detail.originalEvent.clientX - rect.left;
		const y = event.detail.originalEvent.clientY - rect.top;

		console.log(`Clicked at x: ${x}, y: ${y}`);

		this.georef.x = x;
		this.georef.y = y;
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
